/**
 * Task Detail Page
 *
 * Complete detail view for a single task record with 5 tabs.
 * UC10: Cancel Task with reason (manager/cb_supervisor only).
 * UC11: Reassign Task (manager/cb_supervisor only).
 * UC8:  On Credit Block task completion, auto-create follow-up "send confirmation email" task.
 * UC3:  Audit trail via logStatusChanged / logAssignmentChanged.
 */

import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { persistedTaskRecords } from '../../services/mockData/tasks';
import { getRecordById, deleteRecord, updateRecord } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { LayoutSection } from '../../components/detail/LayoutSection';
import { HistoryTab } from '../../components/detail/HistoryTab';
import { usePageLayout, usePageLayoutTabs } from '../../hooks/useObjectManager';
import { TaskModal } from '../../components/modals/TaskModal';
import { CheckSquare, Link2, List, StickyNote, XCircle, UserPlus, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { logStatusChanged, logAssignmentChanged } from '../../services/activityLogService';
import { createFollowUpTaskForCreditBlockCase } from '../../services/creditBlock/creditBlockOpsIntake';
import { applyTaskAssigneeFromPicker } from '../../services/creditBlock/creditBlockTaskRelations';
import { sendEmail } from '../../services/emailSendService';
import { SearchableSelect } from '../../components/modals/SearchableSelect';
import GobodoConfig from '../../config/gobodoConfig';
import {
  searchCreditBlockAssigneeOptions,
  usersToCreditBlockAssigneeOptions,
} from '../../services/gobodo/creditBlockNewCaseGobodoSearch';
import {
  CREDIT_BLOCK_ASSIGNEE_USER_FILTERS,
  ownerSelectOptionsForAssignee,
  parseOwnerPick,
} from '../../services/creditBlock/creditBlockAssigneePickerUtils';
import { fetchUsersForUserManagement, userKeys } from '../../services/queries/userQueries';

const MANAGER_ROLES = new Set(['manager', 'cb_supervisor', 'cm_supervisor', 'system_admin']);

function isCreditBlockTask(task: Record<string, unknown>): boolean {
  const src = String(task.Source__c ?? task.source ?? '').toLowerCase();
  const type = String(task.Type__c ?? task.type ?? '').toLowerCase();
  return src.includes('credit_block') || type.includes('credit block') || type.includes('lock');
}

function getRelatedCaseId(task: Record<string, unknown>): string | null {
  const id = String(task.RelatedTo__c ?? task.relatedTo ?? '').trim();
  const objType = String(task.RelatedObjectType__c ?? '').trim();
  if (id && (objType === 'Case__c' || objType === '' || !objType)) return id;
  return null;
}

function isConfirmationEmailTask(task: Record<string, unknown>): boolean {
  const subj = String(task.title ?? task.Subject__c ?? task.subject ?? '').toLowerCase();
  return subj.includes('send confirmation email') || subj.includes('confirmation email');
}

async function trySendConfirmationEmail(task: Record<string, unknown>): Promise<boolean> {
  const desc = String(task.Description__c ?? task.description ?? '');
  const clientEmail = String(task.clientEmail ?? '').trim();
  const recipientEmail = clientEmail || 'client@example.com';
  const taskSubj = String(task.title ?? task.Subject__c ?? task.subject ?? '');
  try {
    await sendEmail({
      to: [recipientEmail],
      subject: `Credit Block Confirmation: ${taskSubj}`,
      message: `Dear client,\n\nThis is to confirm that the requested action has been completed.\n\n${desc}\n\nThank you,\nRegal Credit Management`,
    });
    return true;
  } catch {
    return false;
  }
}

interface TaskDetailPageProps {
  id: string;
  onBack: () => void;
  userRole?: string;
  onNavigateToClient?: (clientId: string) => void;
}

export const TaskDetailPage: React.FC<TaskDetailPageProps> = ({ id, onBack, userRole = 'employee', onNavigateToClient }) => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const { userId, userName } = useCurrentUser();
  const queryClient = useQueryClient();
  const tabs = usePageLayoutTabs('Task__c');
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 'overview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  /** SearchableSelect value: `u:{graphUserKey}` or `n:{encodedName}`. */
  const [assigneePick, setAssigneePick] = useState('');
  const [assigneeSearchRaw, setAssigneeSearchRaw] = useState('');
  const debouncedAssigneeSearch = useDebouncedValue(assigneeSearchRaw, 250);
  const [actionBusy, setActionBusy] = useState(false);
  const canManage = MANAGER_ROLES.has(userRole);

  const useGobodo = shouldUseGobodo();

  const { data: adminUsers = [], isLoading: adminUsersLoading } = useQuery({
    queryKey: userKeys.list(CREDIT_BLOCK_ASSIGNEE_USER_FILTERS),
    queryFn: () => fetchUsersForUserManagement(CREDIT_BLOCK_ASSIGNEE_USER_FILTERS),
    enabled: reassignModalOpen,
    staleTime: 5 * 60 * 1000,
  });

  const adminUserPickerOptions = useMemo(() => usersToCreditBlockAssigneeOptions(adminUsers), [adminUsers]);

  const { data: assigneeRemoteOptions = [], isFetching: assigneeRemoteFetching } = useQuery({
    queryKey: [
      'gobodo',
      'UsersApi',
      'searchUsers',
      'taskDetailReassign',
      GobodoConfig.graph(),
      debouncedAssigneeSearch,
    ],
    queryFn: () => searchCreditBlockAssigneeOptions(debouncedAssigneeSearch),
    enabled: reassignModalOpen && useGobodo && debouncedAssigneeSearch.trim().length > 0,
    staleTime: 60_000,
  });

  const assigneePickerOptions = useMemo(() => {
    if (useGobodo) {
      if (!debouncedAssigneeSearch.trim()) return adminUserPickerOptions;
      return assigneeRemoteOptions;
    }
    const q = debouncedAssigneeSearch.trim().toLowerCase();
    if (!q) return adminUserPickerOptions;
    return adminUserPickerOptions.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [useGobodo, debouncedAssigneeSearch, adminUserPickerOptions, assigneeRemoteOptions]);

  const assigneeOwnersLoading = useGobodo
    ? debouncedAssigneeSearch.trim()
      ? assigneeRemoteFetching
      : adminUsersLoading
    : adminUsersLoading;

  const assigneeRows = useMemo(
    () => ownerSelectOptionsForAssignee(assigneePickerOptions, userName),
    [assigneePickerOptions, userName]
  );

  const { data: task, isLoading } = useQuery({
    queryKey: ['task', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const record = await getRecordById('Task', id);
        if (!record) throw new Error('Task not found');
        const r = record as Record<string, any>;
        return {
          ...record,
          title: r.title ?? r.Subject__c ?? r.subject ?? r.label,
          status: r.status ?? r.Status__c,
          priority: r.priority ?? r.Priority__c,
          dueDate: r.dueDate ?? r.DueDate__c,
          assignee: r.assignee ?? r.Assignee__c ?? r.Owner__c,
          description: r.description ?? r.Description__c,
          type: r.type ?? r.Type__c,
          source: r.source ?? r.Source__c,
          clientName: r.clientName ?? r.ClientName__c ?? r.AccountName__c,
          clientId: r.clientId ?? r.ClientId__c ?? r.AccountId__c,
          completedDate: r.completedDate ?? r.CompletedDate__c,
          createdBy: r.createdBy ?? r.CreatedBy__c,
          createdDate: r.createdDate ?? r.CreatedDate__c,
          lastModifiedDate: r.lastModifiedDate ?? r.LastModified__c ?? r.lastModified,
          RelatedTo__c: r.RelatedTo__c ?? r.relatedTo,
        };
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allTasks = persistedTaskRecords.get();
      const found = allTasks.find(t => t.id === id);
      if (!found) throw new Error('Task not found');
      return found;
    }
  });

  // Fetch page layout (uses Record Pages assignment system)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Task__c', task);

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    const displayTitle = task?.title ?? (task as any)?.Subject__c ?? task?.subject ?? 'this task';
    if (!window.confirm(`Are you sure you want to delete "${displayTitle}"?`)) return;
    if (shouldUseGobodo()) {
      deleteRecord('Task__c', id)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
          queryClient.invalidateQueries({ queryKey: ['taskListViewData'] });
          queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Task__c'] });
          success('Task deleted', `"${displayTitle}" has been deleted`);
          onBack();
        })
        .catch((err) => {
          console.error('[TaskDetailPage] Delete failed:', err);
          success('Error', `Failed to delete task: ${err?.message || 'Unknown error'}`);
        });
      return;
    }
    const allTasks = persistedTaskRecords.get();
    persistedTaskRecords.set(allTasks.filter(t => t.id !== id));
    queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
    queryClient.invalidateQueries({ queryKey: ['taskListViewData'] });
    success('Task deleted', `"${displayTitle}" has been deleted`);
    onBack();
  };

  const invalidateTaskQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['task', id] });
    queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
    queryClient.invalidateQueries({ queryKey: ['taskListViewData'] });
    queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Task__c'] });
    queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Task'] });
    queryClient.invalidateQueries({ queryKey: ['credit-block'] });
  };

  const handleComplete = async () => {
    if (!task) return;
    const oldStatus = String(task.status ?? task.Status__c ?? 'Open');
    const now = new Date().toISOString();
    try {
      if (shouldUseGobodo()) {
        await updateRecord(
          'Task__c',
          id,
          { status: 'Completed', Status__c: 'Completed', completedDate: now, lastModified: now },
          { gobodoPropertyPatch: true }
        );
      } else {
        const allTasks = persistedTaskRecords.get();
        persistedTaskRecords.set(allTasks.map(t => t.id === id ? { ...t, status: 'Completed', completedDate: now, lastModifiedDate: now } : t));
      }
      logStatusChanged(id, 'Task__c', oldStatus, 'Completed', userId, userName);
      invalidateTaskQueries();

      if (isCreditBlockTask(task)) {
        if (isConfirmationEmailTask(task)) {
          const emailSent = await trySendConfirmationEmail(task);
          success(
            'Task completed',
            emailSent ? 'Confirmation email sent to client' : 'Marked complete (email sending not available — send manually)'
          );
        } else {
          const caseId = getRelatedCaseId(task);
          if (caseId) {
            try {
              const clientName = String(task.clientName ?? task.AccountName__c ?? '').trim();
              const subj = [clientName || null, 'Send confirmation email'].filter(Boolean).join(' · ');
              await createFollowUpTaskForCreditBlockCase({
                caseId,
                assigneeName: '',
                taskSubject: subj,
                description: `Follow-up: send confirmation email to client after task "${String(task.title ?? task.Subject__c ?? '')}". Case ${caseId}.`,
                priority: 'High',
                actor: { userId, userName, role: 'credit_block' },
                parentTaskId: id,
              });
              success('Task completed', 'Marked complete — confirmation email task created for manager assignment');
            } catch {
              success('Task completed', 'Marked complete — follow-up task creation failed (check manually)');
            }
          } else {
            success('Task completed', 'Task has been marked as complete');
          }
        }
      } else {
        success('Task completed', 'Task has been marked as complete');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showError('Complete failed', msg);
    }
  };

  const handleCancel = async () => {
    if (!task || !cancelReason.trim()) return;
    setActionBusy(true);
    const oldStatus = String(task.status ?? task.Status__c ?? 'Open');
    const now = new Date().toISOString();
    try {
      if (shouldUseGobodo()) {
        await updateRecord(
          'Task__c',
          id,
          {
            status: 'Cancelled',
            Status__c: 'Cancelled',
            CancellationReason__c: cancelReason.trim(),
            cancellationReason: cancelReason.trim(),
            lastModified: now,
          },
          { gobodoPropertyPatch: true }
        );
      } else {
        const allTasks = persistedTaskRecords.get();
        persistedTaskRecords.set(allTasks.map(t => t.id === id
          ? { ...t, status: 'Cancelled', cancellationReason: cancelReason.trim(), lastModifiedDate: now } : t));
      }
      logStatusChanged(id, 'Task__c', oldStatus, 'Cancelled', userId, userName);
      invalidateTaskQueries();
      success('Task cancelled', cancelReason.trim());
      setCancelModalOpen(false);
      setCancelReason('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showError('Cancel failed', msg);
    } finally {
      setActionBusy(false);
    }
  };

  const handleReassign = async () => {
    if (!task || !assigneePick.trim()) return;
    if (useGobodo && !assigneePick.startsWith('u:')) {
      showError('Reassign failed', 'Select a user from the list so the assignee can be linked in Gobodo.');
      return;
    }
    setActionBusy(true);
    const oldAssignee = String(task.assignee ?? task.Assignee__c ?? task.Owner__c ?? '');
    const { ownerName } = parseOwnerPick(assigneePick, assigneeRows);
    const displayAssignee = ownerName.trim() || assigneePick;
    try {
      if (useGobodo) {
        const payload: Record<string, unknown> = {};
        applyTaskAssigneeFromPicker(payload, {
          assigneePickRaw: assigneePick.startsWith('u:') ? assigneePick : undefined,
          assigneeDisplayName: displayAssignee,
        });
        await updateRecord('Task__c', id, payload, { gobodoPropertyPatch: true });
      } else {
        const allTasks = persistedTaskRecords.get();
        persistedTaskRecords.set(
          allTasks.map((t) =>
            t.id === id ? { ...t, assignee: displayAssignee, owner: displayAssignee } : t
          )
        );
      }
      logAssignmentChanged(id, 'Task__c', oldAssignee, displayAssignee, userId, userName);
      invalidateTaskQueries();
      success('Task reassigned', `Now assigned to ${displayAssignee}`);
      setReassignModalOpen(false);
      setAssigneePick('');
      setAssigneeSearchRaw('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showError('Reassign failed', msg);
    } finally {
      setActionBusy(false);
    }
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Tasks">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!task) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Tasks">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Task not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  // Status badge colors
  const statusColors: Record<string, string> = {
    'Open': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'In Progress': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    'Completed': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    'Cancelled': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  };

  // Priority badge colors
  const priorityColors: Record<string, string> = {
    'Low': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300',
    'Medium': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'High': 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
  };

  // Format date for subtitle
  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'No due date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverview = ['overview', 'tab_001', 'details'].includes(activeTab);
  const isRelated = ['related', 'tab_002'].includes(activeTab);
  const isSubtasks = ['subtasks'].includes(activeTab);
  const isNotes = ['notes', 'tab_004'].includes(activeTab);
  const isHistory = ['history'].includes(activeTab);

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Tasks">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <DetailHeader
          title={task.title as string}
          subtitle={`Due: ${formatDate(task.dueDate as string)}`}
          recordId={task.id as string}
          status={task.status as string}
          statusColor={statusColors[task.status as string] || statusColors.Open}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Priority + Client link */}
        {(task.priority || (isCreditBlockTask(task) && task.clientId)) && (
          <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-wrap items-center gap-3">
            {task.priority && (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${priorityColors[task.priority as string] || priorityColors.Medium}`}>
                {task.priority} Priority
              </span>
            )}
            {isCreditBlockTask(task) && task.clientId && (
              <button
                type="button"
                onClick={() => {
                  const cid = String(task.clientId);
                  if (onNavigateToClient) {
                    onNavigateToClient(cid);
                  } else {
                    navigate(`/clients/${cid}`);
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {task.clientName ? `Client: ${task.clientName}` : 'Go to client'}
              </button>
            )}
          </div>
        )}

        {/* Task Actions (if not completed/cancelled) */}
        {task.status !== 'Completed' && task.status !== 'Cancelled' && (
          <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-wrap gap-3">
            <button
              onClick={handleComplete}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <CheckSquare className="w-4 h-4" />
              Mark as Complete
            </button>
            {canManage && (
              <button
                onClick={() => setCancelModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                <XCircle className="w-4 h-4" />
                Cancel Task
              </button>
            )}
            {canManage && (
              <button
                onClick={() => {
                  setAssigneePick('');
                  setAssigneeSearchRaw('');
                  setReassignModalOpen(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <UserPlus className="w-4 h-4" />
                Reassign
              </button>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {isOverview && (
              <div className="space-y-6">
                {loadingLayout ? (
                  // Loading state
                  <div className="space-y-6">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6"
                      >
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Render sections from page layout
                  pageLayout?.sections.map(section => (
                    <LayoutSection
                      key={section.id}
                      section={section}
                      objectApiName="Task"
                      record={task}
                    />
                  ))
                )}
              </div>
            )}

            {isRelated && (
              <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
                <div className="text-center">
                  <Link2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No related records
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Related records for this task will appear here.
                  </p>
                </div>
              </div>
            )}

            {isSubtasks && (
              <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
                <div className="text-center">
                  <List className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No subtasks yet
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Break down this task into smaller subtasks.
                  </p>
                </div>
              </div>
            )}

            {isNotes && (
              <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
                <div className="text-center">
                  <StickyNote className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No notes yet
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Task notes will appear here.
                  </p>
                </div>
              </div>
            )}

            {isHistory && <HistoryTab recordId={id} objectType="Task__c" />}
          </div>
        </div>
      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        task={task}
      />

      {/* Cancel Task Modal (UC10) */}
      {cancelModalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50" onClick={() => setCancelModalOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Cancel Task</h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Cancellation reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                placeholder="Why is this task being cancelled?"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end gap-3">
              <button type="button" onClick={() => setCancelModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                Close
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={!cancelReason.trim() || actionBusy}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {actionBusy ? 'Cancelling…' : 'Cancel Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Task Modal (UC11) */}
      {reassignModalOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50"
          onClick={() => {
            setReassignModalOpen(false);
            setAssigneeSearchRaw('');
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reassign Task</h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              {String(task.assignee ?? task.Assignee__c ?? '').trim() ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Current assignee:{' '}
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {String(task.assignee ?? task.Assignee__c ?? '')}
                  </span>
                </p>
              ) : null}
              <SearchableSelect
                label="New assignee"
                name="assigneePick"
                value={assigneePick}
                onChange={setAssigneePick}
                options={assigneeRows}
                skipLocalFilter
                onSearchTermChange={setAssigneeSearchRaw}
                placeholder={
                  assigneeOwnersLoading && assigneeRows.length === 0
                    ? 'Loading users…'
                    : useGobodo && debouncedAssigneeSearch.trim() && assigneeRemoteFetching
                      ? 'Searching users…'
                      : 'Search users (same list as Admin → Users)…'
                }
                required
              />
              {useGobodo ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Pick someone from the list to set assignee text and the Gobodo user link on the task (if configured).
                </p>
              ) : null}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setReassignModalOpen(false);
                  setAssigneeSearchRaw('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleReassign}
                disabled={
                  !assigneePick.trim() ||
                  actionBusy ||
                  (useGobodo && !assigneePick.startsWith('u:'))
                }
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                {actionBusy ? 'Reassigning…' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
};
